import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ActionsGrid from 'components/ActionsGrid'
import {
  selectIsLoadingGroups,
  selectIsLoadingZones,
  setIsInitialized,
  addAlert,
  fetchCurrentUser,
  selectAvailableZones,
  fetchZones,
  fetchGroups,
  selectCurrentUser,
} from 'store/global'
import { usePermissions } from 'hooks/usePermissions'
import {
  selectDisplayedGroup,
  setDisplayedGroup,
  closeDisplayedGroup,
  deleteGroup,
  editGroup,
  createGroup,
} from 'store/organizations'
import messages from './messages'
import { selectAllGroups } from 'store/global'
import { COLUMN_ZONES, gridColumns } from './specs'
import GroupModal from 'components/GroupModal/GroupModal'
import Loader from 'components/Loader/Loader'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import Input from 'components/Input'
import { orderBy } from 'lodash'
import styles from './Groups.module.scss'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'
import Button from 'components/Button'
import Icon from 'components/Icon'

export default function Groups() {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const isLoading = useSelector(selectIsLoadingGroups)
  const isLoadingZones = useSelector(selectIsLoadingZones)
  const displayedGroup = useSelector(selectDisplayedGroup)
  const allGroups = useSelector(selectAllGroups)
  const availableZones = useSelector(selectAvailableZones) || []
  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  const sortableColumns = new Set(['name'])

  useEffect(() => {
    dispatch(closeDisplayedGroup())
    dispatch(setIsInitialized(true))
    dispatch(fetchZones())
    dispatch(fetchGroups())
  }, [])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const flattenGroups = groups => {
    let flattened = []

    groups.forEach(group => {
      const groupWithLevel = {
        ...group,
        displayName: group.path,
        canEdit: group.canEdit || false,
        canDelete: group.canDelete || false,
      }
      flattened.push(groupWithLevel)

      if (group.subGroups && group.subGroups.length > 0) {
        flattened = flattened.concat(flattenGroups(group.subGroups))
      }
    })
    return flattened
  }

  const flattenedGroups = React.useMemo(() => {
    if (!allGroups) return []
    const flattened = flattenGroups(allGroups)
    return flattened
  }, [allGroups])

  const filteredGroups = React.useMemo(() => {
    if (!searchTerm) return flattenedGroups

    const searchLower = searchTerm.toLowerCase()
    return flattenedGroups.filter(
      group => group.name.toLowerCase().includes(searchLower) || group.displayName.toLowerCase().includes(searchLower),
    )
  }, [flattenedGroups, searchTerm])

  const sortedGroups = React.useMemo(() => {
    if (!sortColumn) return filteredGroups
    return orderBy(filteredGroups, [sortColumn], [sortOrder])
  }, [filteredGroups, sortColumn, sortOrder])

  const onDisplayModal = (action = 'create', existingGroup = {}) => {
    const newGroup = {
      ...existingGroup,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedGroup({ group: newGroup }))
  }

  const onSubmit = async group => {
    if (displayedGroup.action === 'delete') {
      dispatch(deleteGroup({ groupId: group.id }))
    } else if (displayedGroup.action === 'create') {
      dispatch(createGroup({ group }))
    } else {
      dispatch(editGroup({ group }))
    }

    dispatch(addAlert({ type: 'success', message: 'Group Action Performed' }))
    dispatch(setDisplayedGroup())
    dispatch(fetchCurrentUser())
  }

  const onExportCSV = () => {
    if (!sortedGroups.length) return
    const exportData = sortedGroups.map(group => {
      const zoneNames = (group.zoneIds || [])
        .map(zoneId => {
          const zone = availableZones.find(z => z.id === zoneId)
          return zone ? zone.name : zoneId
        })
        .join(', ')

      return {
        name: group.displayName,
        zones: zoneNames,
      }
    })

    const fields = ['name', 'zones']
    const parser = new Parser({ fields })
    const csv = parser.parse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'groups.csv')
  }

  const renderGridCell = (group, column) => {
    if (column === 'name') {
      return <div className={styles.groupName}>{group.displayName}</div>
    }
    if (column === COLUMN_ZONES) {
      const zoneNames = (group.zoneIds || [])
        .map(zoneId => {
          const zone = availableZones.find(z => z.id === zoneId)
          return zone ? zone.name : zoneId
        })
        .join(', ')

      return zoneNames || ''
    }
    if (column === 'actions') {
      return (
        <div className={styles.actions}>
          {group.canEdit === true && (
            <Button color='primary' outline onClick={() => onDisplayModal('edit', group)}>
              <Icon name='edit' size='regular'>
                <FormattedMessage id='app.containers.Groups.actions.edit' defaultMessage='Edit' />
              </Icon>
            </Button>
          )}
          {group.canDelete === true && (
            <Button color='danger' outline onClick={() => onDisplayModal('delete', group)}>
              <Icon name='delete' size='regular'>
                <FormattedMessage id='app.containers.Groups.actions.delete' defaultMessage='Delete' />
              </Icon>
            </Button>
          )}
        </div>
      )
    }
    return group[column]
  }

  if (isLoading || isLoadingZones) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader type='globalLoading' message='Loading groups and zones...' />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Input
        placeholder='Input search text...'
        value={searchTerm}
        onChange={value => setSearchTerm(value)}
        className={styles.search}
      />

      <ActionsGrid
        isLoading={isLoading}
        type='group'
        rows={sortedGroups}
        columns={gridColumns}
        header={<FormattedMessage {...messages.header} />}
        onAction={onDisplayModal}
        onSort={onSort}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onExportCSV={onExportCSV}
        showCreateButton={permissions.admin}
      >
        {(group, columnKey, isHeader) =>
          isHeader ? (
            <div>
              {sortableColumns.has(columnKey) ? (
                <div className={styles.header} onClick={() => onSort(columnKey)}>
                  <FormattedMessage {...messages.columns[columnKey]} /> <SwapVertIcon fontSize='small' />
                </div>
              ) : (
                <FormattedMessage {...messages.columns[columnKey]} />
              )}
            </div>
          ) : (
            renderGridCell(group, columnKey)
          )
        }
      </ActionsGrid>
      {displayedGroup.isOpen && <GroupModal mode={displayedGroup.action} onSubmit={onSubmit} />}
    </div>
  )
}
